/**
 * Hàm hình học thuần — không state, không I/O, không browser API.
 *
 * **Aspect ratio correction (DU #4).** Landmark ở toạ độ chuẩn hoá: `x` theo chiều rộng,
 * `y` theo chiều cao. Trên frame 16:9, 0.1 đơn vị x dài gần gấp đôi 0.1 đơn vị y, nên tính
 * góc trực tiếp từ (x, y) sẽ ra góc **méo theo tỉ lệ khung ảnh** — cùng một tư thế đo ở
 * portrait và landscape cho hai con số khác nhau. Mọi hàm ở đây quy x về cùng đơn vị với y
 * bằng cách nhân `aspectRatio` (= videoWidth / videoHeight) trước khi tính.
 *
 * Baseline có hấp thụ được một phần sai lệch này, nhưng chỉ khi trẻ không xoay điện thoại
 * giữa phiên — điều không thể bảo đảm.
 */

import type { BodyLandmark } from "@studystraight/shared-types";

const RAD_TO_DEG = 180 / Math.PI;

/** Dưới ngưỡng này thì hai điểm coi như trùng nhau và mọi góc suy ra từ chúng là vô nghĩa. */
const MIN_VECTOR_LENGTH = 1e-6;

/** Điểm 2D đã quy về đơn vị chiều cao frame. */
interface Vec2 {
  x: number;
  y: number;
}

function toUniform(p: BodyLandmark, aspectRatio: number): Vec2 {
  return { x: p.x * aspectRatio, y: p.y };
}

/**
 * Trung điểm. `visibility` lấy **min** của hai đầu, không phải trung bình: một điểm bị che
 * hoàn toàn thì trung điểm cũng không đáng tin, dù điểm kia rõ tới đâu.
 */
export function midpoint(a: BodyLandmark, b: BodyLandmark): BodyLandmark {
  const out: BodyLandmark = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  if (a.z !== undefined && b.z !== undefined) out.z = (a.z + b.z) / 2;
  if (a.visibility !== undefined || b.visibility !== undefined) {
    out.visibility = Math.min(a.visibility ?? 0, b.visibility ?? 0);
  }
  return out;
}

/** Khoảng cách theo đơn vị chiều cao frame. */
export function distance(a: BodyLandmark, b: BodyLandmark, aspectRatio = 1): number {
  const dx = (b.x - a.x) * aspectRatio;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

/**
 * Góc (độ, 0..180) tại `vertex` giữa hai tia `vertex→a` và `vertex→b`.
 * Trả `NaN` nếu một trong hai tia suy biến — xem `signedAngleFromVertical`.
 */
export function angleBetween(
  a: BodyLandmark,
  vertex: BodyLandmark,
  b: BodyLandmark,
  aspectRatio = 1,
): number {
  const v = toUniform(vertex, aspectRatio);
  const u1 = { x: a.x * aspectRatio - v.x, y: a.y - v.y };
  const u2 = { x: b.x * aspectRatio - v.x, y: b.y - v.y };
  const len1 = Math.hypot(u1.x, u1.y);
  const len2 = Math.hypot(u2.x, u2.y);
  if (len1 < MIN_VECTOR_LENGTH || len2 < MIN_VECTOR_LENGTH) return Number.NaN;

  const cos = (u1.x * u2.x + u1.y * u2.y) / (len1 * len2);
  // Clamp: sai số dấu phẩy động có thể đẩy cos ra ngoài [-1, 1] → acos trả NaN.
  return Math.acos(Math.min(1, Math.max(-1, cos))) * RAD_TO_DEG;
}

/**
 * Góc có dấu (độ, -180..180) giữa vector `from→to` và trục dọc **hướng xuống** của ảnh.
 * 0 = thẳng đứng hướng xuống. Dấu dương = lệch sang phải trong ảnh.
 *
 * Trả `NaN` khi hai điểm trùng nhau: đó là "không đo được", và caller phải chuyển nó thành
 * `Metric.available = false` chứ không phải 0 độ — 0 độ nghĩa là "tư thế hoàn hảo", một giá trị
 * mặc định sai lệch nguy hiểm hơn hẳn việc thiếu dữ liệu.
 */
export function signedAngleFromVertical(
  from: BodyLandmark,
  to: BodyLandmark,
  aspectRatio = 1,
): number {
  const dx = (to.x - from.x) * aspectRatio;
  const dy = to.y - from.y;
  if (Math.hypot(dx, dy) < MIN_VECTOR_LENGTH) return Number.NaN;
  return Math.atan2(dx, dy) * RAD_TO_DEG;
}

/** Độ lệch khỏi trục dọc, không dấu (0..180). */
export function angleFromVertical(
  from: BodyLandmark,
  to: BodyLandmark,
  aspectRatio = 1,
): number {
  return Math.abs(signedAngleFromVertical(from, to, aspectRatio));
}

/**
 * Góc có dấu (độ, -90..90) của đoạn `a→b` so với trục ngang — dùng cho shoulder tilt (§13).
 * Dấu dương = `b` thấp hơn `a` trong ảnh.
 */
export function signedAngleFromHorizontal(
  a: BodyLandmark,
  b: BodyLandmark,
  aspectRatio = 1,
): number {
  const dx = (b.x - a.x) * aspectRatio;
  const dy = b.y - a.y;
  if (Math.hypot(dx, dy) < MIN_VECTOR_LENGTH) return Number.NaN;
  return Math.atan2(dy, Math.abs(dx)) * RAD_TO_DEG;
}

/**
 * Khoảng cách vuông góc từ `point` tới đường thẳng qua `a`–`b` (đơn vị chiều cao frame).
 * Dùng cho mode SIDE: độ nhô của mũi ra khỏi trục thân (vai↔hông) chính là "đầu chìa về
 * phía màn hình" — thứ thay cho ear-distance ở góc nhìn ngang, nơi hai tai chồng nhau.
 *
 * Trả `NaN` khi `a` và `b` trùng nhau (không xác định được đường thẳng).
 */
export function perpendicularDistance(
  point: BodyLandmark,
  a: BodyLandmark,
  b: BodyLandmark,
  aspectRatio = 1,
): number {
  const ax = a.x * aspectRatio;
  const bx = b.x * aspectRatio;
  const px = point.x * aspectRatio;
  const dx = bx - ax;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < MIN_VECTOR_LENGTH) return Number.NaN;
  return Math.abs(dx * (a.y - point.y) - dy * (ax - px)) / len;
}

/** Trung vị. Không mutate input. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] as number;
  return ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/**
 * Median absolute deviation. Dùng cho calibration (§11) và cho ngưỡng ổn định của baseline —
 * xem `CalibrationConfig.outlierMadFactor`.
 */
export function medianAbsoluteDeviation(values: readonly number[], center?: number): number {
  if (values.length === 0) return Number.NaN;
  const c = center ?? median(values);
  return median(values.map((v) => Math.abs(v - c)));
}
